import { Box, Collapse, IconButton, Typography } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { WD } from '../../../warningDashboard/warningTheme';
import { DUE_COLOR, FIGURE, LEVEL_TONE, STATUS } from '../utils/constants';
import { dueText, formatShort } from '../utils/dates';
import { fmtHours, isPaused } from '../utils/hours';
import { isHandedOver } from '../utils/tasks';
import StatusTag from './StatusTag';
import TaskHours from './TaskHours';

/**
 * One task in a person's drawer: title, status, project, due (or completed) date, and the hours - logged / planned, "No hours"
 * when a head still has to plan it, "No time logged" when it is finished or handed over without any. Opens to `TaskHours`.
 */
export default function TaskItem({ item, row, idx, today, canLog, last, open, onToggle, onChanged }) {
  const { task, plan } = item;
  const done = task.status === STATUS.COMPLETED;
  const handedOver = isHandedOver(task);
  // a handed-over task is not late on the person any more, so its due date is never coloured
  const due = handedOver ? { text: task.dueDate ? formatShort(task.dueDate) : null, tone: 'ok' } : dueText(task.dueDate, today);
  const over = plan && plan.logged > plan.plannedHours;
  const mayExpand = row.canPlan || (plan && canLog);

  return (
    <Box sx={{ borderBottom: last ? 'none' : `1px solid ${WD.border}` }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5} sx={{ py: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: WD.ink }}>{task.title}</Typography>
          <Box display="flex" alignItems="center" gap={0.75} sx={{ mt: 0.5, minWidth: 0 }}>
            <StatusTag status={task.status} />
            <Typography noWrap sx={{ minWidth: 0, fontSize: 12, color: WD.inkFaint }}>
              {task.projectName}
              {done && task.completedOn ? (
                <> · completed {formatShort(task.completedOn)}</>
              ) : (
                due.text && <Box component="span" sx={{ color: DUE_COLOR[due.tone] }}> · due {due.text}</Box>
              )}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
          {plan ? (
            <Typography component="span" sx={{ textAlign: 'right', fontSize: 12.5, ...FIGURE }}>
              <Box component="span" sx={{ fontWeight: 700, color: over ? LEVEL_TONE.overloaded.text : WD.ink }}>{fmtHours(plan.logged)}</Box>
              <Box component="span" sx={{ color: WD.inkFaint }}> / {fmtHours(plan.plannedHours)}</Box>
              {isPaused(plan) && <Box component="span" sx={{ display: 'block', fontSize: 10.5, fontWeight: 700, color: LEVEL_TONE.atCapacity.text }}>paused</Box>}
            </Typography>
          ) : handedOver ? (
            // finished or handed over with no hours: nothing is missing, it simply had none
            <Typography component="span" sx={{ fontSize: 11.5, color: WD.inkFaint }}>No time logged</Typography>
          ) : (
            <Box component="span" sx={{ borderRadius: 1, px: 0.75, py: 0.25, fontSize: 11, fontWeight: 700, bgcolor: LEVEL_TONE.atCapacity.bg, color: LEVEL_TONE.atCapacity.text }}>
              No hours
            </Box>
          )}
          {mayExpand && (
            <IconButton size="small" onClick={onToggle} aria-expanded={open} aria-label={open ? 'Hide hours' : 'Show hours'} sx={{ border: `1px solid ${WD.border}` }}>
              <ExpandMoreRoundedIcon fontSize="small" sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={open} unmountOnExit>
        <TaskHours task={task} plan={plan} row={row} idx={idx} today={today} canLog={canLog} onChanged={onChanged} />
      </Collapse>
    </Box>
  );
}
